// supervisor-release-approved-g3-leg.ts
//
// The "G3 approved" branch of resumeReleaseFlowIfConfigured, split out to keep
// supervisor-release-flow-resume.ts under the 200-line budget. H14 bản 2
// (2026-09-29) — every state says what actually happened:
//   - no deploy_recipe ⇒ `deploy_not_configured`: 0 intents, 0 receipts (the
//     old code ran `true` and wrote `deployed_verified`);
//   - deploy exit 0 ⇒ deploy receipt (bound to the DEPLOY hash, not the full
//     manifest — review M1) written BEFORE smoke; smoke fails ⇒
//     `deploy_smoke_failed`, and the next run re-runs ONLY smoke (B2-2);
//   - an approval's age only matters before a side effect: no deploy_recipe ⇒
//     done without deploy as long as G3 is bound to this candidate + manifest
//     (review M2a); deploy already ran but G3 no longer rechecks ⇒
//     `release_g3_invalid_after_deploy`, never "deploy blocked" (M2b);
//   - an older CLI's `deployed_verified` receipt ⇒ `release_unverified_legacy`:
//     still no repeat deploy, never counted as released (B2-3);
//   - a deploy whose outcome is unknown (run died mid-deploy, timeout,
//     damaged receipt) ⇒ `release_deploy_outcome_unknown`, never re-run; one
//     the journal proves exited 0 only gets its missing receipt (Luke 30/09);
//   - rollout is `not_observed` (v1 has no probe), never self-matched;
//   - the first deploy waits for a CI pass of the pushed commit's MR pipeline
//     (`verifyCi`, dogfood 2026-10-01): not a pass ⇒ no deploy, a re-run re-checks.
import { recheckG3ImmediatelyBeforeFirstSideEffect } from "./deployment-adapter.js";
import type { MissionStore } from "./mission-store.js";
import { resolveRecipeCwdAgainstRepoRoot } from "./recipe-cwd-resolved-against-repo-root.js";
import {
  readReleaseReceiptState,
  recordDeployReceipt,
  recordSmokeReceipt,
} from "./release-deploy-completion-receipt.js";
import {
  assessDeployOutcome,
  deployOutcomeUnknownReason,
} from "./release-deploy-outcome-assessment.js";
import {
  computeReleaseDeployHash,
  g3ArtifactHash,
  type ReleaseConfig,
} from "./release-manifest-hash.js";
import { deployInput, recipeTimeoutMs } from "./release-recipe-run-inputs.js";
import { releaseWorktreeDirtyReason } from "./release-worktree-clean-before-deploy.js";
import { runInterruptReceived } from "./run-interrupt-signal-handler.js";
import { runReleaseDeployStep, runReleaseSmokeStep } from "./supervisor-release-deploy-verify.js";
import type {
  ReleaseCiNotPassedOutcome,
  ReleaseFlowResumeOutcome,
} from "./supervisor-release-flow-resume-outcome.js";

export const LEGACY_RECEIPT_REASON =
  "receipt deploy cũ (`deployed_verified`, CLI trước H14) không chứng minh recipe đã chạy thật — " +
  "bản cũ ghi receipt này cả khi chưa cấu hình deploy_recipe (chạy `true`). Không deploy lại. " +
  "Kiểm tay môi trường đích rồi `avatar autopilot archive`.";

export interface ApprovedG3LegInput {
  store: MissionStore;
  controlRoot: string;
  /** Mission worktree: a relative recipe `cwd` resolves here, like a ticket recipe's (F2). */
  repoDir: string;
  release: ReleaseConfig;
  candidateSha: string;
  releaseManifestHash: string;
  g3RequestId: string;
  /** gates.json g3.artifact_hash — what the approval is bound to. */
  approvedArtifactHash: string | undefined;
  /**
   * CI gate (dogfood 2026-10-01): asked right before the FIRST deploy side
   * effect; `undefined` = the MR's CI passed for the pushed commit, anything
   * else stops the deploy (release-ci-gate-approved-candidate.ts, fail closed).
   */
  verifyCi: () => Promise<ReleaseCiNotPassedOutcome | undefined>;
}

export async function runApprovedG3ReleaseLeg(
  input: ApprovedG3LegInput,
): Promise<ReleaseFlowResumeOutcome> {
  const releaseDeployHash = computeReleaseDeployHash(input.candidateSha, input.release);
  const receipts = await readReleaseReceiptState(input.controlRoot, {
    candidateSha: input.candidateSha,
    releaseManifestHash: input.releaseManifestHash,
    releaseDeployHash,
  });
  if (receipts.legacy) return { kind: "release_unverified_legacy", reason: LEGACY_RECEIPT_REASON };
  // Bug O: released already — a re-run is a quiet no-op, checked before the
  // G3 recheck so an approval that expired AFTER the release stays quiet.
  if (receipts.deployed && receipts.smokePassed) return { kind: "already_deployed" };
  // Luke 2026-09-30: an earlier deploy whose outcome is unknown stops here;
  // one the journal proves exited 0 only gets its missing receipt.
  const journal = await assessDeployOutcome({
    state: input.store.state,
    receipts,
    candidateSha: input.candidateSha,
    releaseDeployHash,
  });
  if (journal.kind === "unknown") {
    return { kind: "release_deploy_outcome_unknown", reason: journal.reason };
  }
  if (journal.kind === "succeeded_without_receipt") {
    await recordDeployReceipt(input.controlRoot, {
      candidateSha: input.candidateSha,
      releaseDeployHash,
      g3RequestId: input.g3RequestId,
      deployRecipeId: journal.recipeId,
      recoveredFromOperationId: journal.operationId,
      manualResolution: journal.manualResolution,
    });
    receipts.deployed = true;
  }

  const deploy = deployInput(input.release, input.repoDir);
  const boundToThisRelease =
    input.approvedArtifactHash === g3ArtifactHash(input.candidateSha, input.releaseManifestHash);
  if (deploy === undefined && boundToThisRelease) return { kind: "deploy_not_configured" };

  const recheckG3 = () =>
    recheckG3ImmediatelyBeforeFirstSideEffect({
      controlRoot: input.controlRoot,
      requestId: input.g3RequestId,
      currentCandidateSha: input.candidateSha,
      currentReleaseManifestHash: input.releaseManifestHash,
      currentTarget: input.release.target_branch,
      approvedTarget: input.release.target_branch,
    });
  const recheck = await recheckG3();
  if (!recheck.ok && receipts.deployed) {
    return {
      kind: "release_g3_invalid_after_deploy",
      reason:
        `${recheck.reason} Deploy đã chạy (có receipt), smoke chưa pass — không chạy smoke khi G3 ` +
        "không còn hợp lệ. Lối thoát: đổi config.release (xin G3 mới) hoặc kiểm tay môi trường rồi `archive`.",
    };
  }
  if (!recheck.ok) return { kind: "deploy_blocked", reason: recheck.reason };
  if (deploy === undefined) return { kind: "deploy_not_configured" };
  // resumeReleaseFlowIfConfigured refuses deploy-without-verify before G3.
  const smokeRecipe = input.release.verify_recipe;
  if (smokeRecipe === undefined) {
    return { kind: "release_flow_blocked", reason: "config.release thiếu verify_recipe." };
  }

  if (!receipts.deployed) {
    // Only a deploy still to come needs a CI pass: with a receipt the side
    // effect already happened, and smoke re-runs regardless of CI.
    const ci = await input.verifyCi();
    if (ci !== undefined) return ci;
    // A3: the recipe runs in the worktree, G3 approved the candidate commit.
    const dirty = await releaseWorktreeDirtyReason(input.repoDir);
    if (dirty !== undefined) return { kind: "deploy_blocked", reason: dirty };
    // CXR-02: the CI check is network I/O — G3 may expire or be revoked while
    // it runs, so read the approval again right before the deploy step (only
    // its own intent journal write sits between this and the spawn).
    const atDeploy = await recheckG3();
    if (!atDeploy.ok) return { kind: "deploy_blocked", reason: atDeploy.reason };
    // CXR-R1: a Ctrl-C that arrived while CI was checked must not start the deploy.
    const interrupted = runInterruptReceived();
    if (interrupted !== undefined) {
      const reason = `Nhận ${interrupted} lúc chờ CI — chưa deploy. Chạy lại \`avatar autopilot run\` để tiếp.`;
      return { kind: "deploy_blocked", reason };
    }
    const deployed = await runReleaseDeployStep({
      store: input.store,
      deploy,
      binding: { candidateSha: input.candidateSha, releaseDeployHash },
    });
    if (deployed.status === "deploy_outcome_unknown") {
      return {
        kind: "release_deploy_outcome_unknown",
        reason: deployOutcomeUnknownReason(deployed.reason, input.candidateSha),
      };
    }
    if (deployed.status === "deploy_failed") {
      return { kind: "deploy_blocked", reason: deployed.reason };
    }
    await recordDeployReceipt(input.controlRoot, {
      candidateSha: input.candidateSha,
      releaseDeployHash,
      g3RequestId: input.g3RequestId,
      deployRecipeId: deploy.recipe.recipe_id,
    });
  }

  const smoke = await runReleaseSmokeStep({
    store: input.store,
    candidateSha: input.candidateSha,
    verify: {
      smokeRecipe: resolveRecipeCwdAgainstRepoRoot(smokeRecipe, input.repoDir),
      env: process.env,
      timeoutMs: recipeTimeoutMs(input.release.verify_timeout_sec),
    },
  });
  if (smoke.status !== "verified") return { kind: "deploy_smoke_failed", reason: smoke.reason };
  await recordSmokeReceipt(input.controlRoot, {
    candidateSha: input.candidateSha,
    releaseManifestHash: input.releaseManifestHash,
    smokeRecipeId: smokeRecipe.recipe_id,
  });
  return { kind: "released" };
}
